import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Sparkles, DollarSign, Video, Globe, Send, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { translations } from "../i18n/translations";
import { CATEGORIES } from "../mock/mock";
import { api } from "../lib/api";
import Header from "../components/public/Header";
import { Footer } from "../components/public/Footer";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";

const COPY = {
  tr: { title: "Uzman Ol", subtitle: "Bilgini gelire dönüştür. Dünyanın her yerinden danışanlarla görüntülü görüşmeler yap.", back: "Ana sayfa",
    perks: ["Fiyatını sen belirle", "Takvimini sen yönet", "Görüntülü 1:1 görüşmeler", "Küresel kitleye eriş"],
    name: "Ad Soyad", email: "E-posta", category: "Kategori", expertise: "Uzmanlık alanların", price: "Görüşme başı hedef ücret ($)", links: "Portföy / sosyal medya linkleri", message: "Kendinden bahset", submit: "Başvuruyu Gönder", success: "Başvurun alındı! En kısa sürede dönüş yapacağız.", sending: "Gönderiliyor..." },
  en: { title: "Become an Expert", subtitle: "Turn your knowledge into income. Meet clients worldwide over video calls.", back: "Home",
    perks: ["Set your own price", "Manage your calendar", "1:1 video consultations", "Reach a global audience"],
    name: "Full Name", email: "Email", category: "Category", expertise: "Your expertise", price: "Target price per session ($)", links: "Portfolio / social links", message: "Tell us about yourself", submit: "Submit Application", success: "Application received! We'll get back to you soon.", sending: "Sending..." },
  ru: { title: "Стать экспертом", subtitle: "Превратите знания в доход. Встречайтесь с клиентами по видеосвязи.", back: "Главная",
    perks: ["Устанавливайте свою цену", "Управляйте календарём", "Видеоконсультации 1:1", "Глобальная аудитория"],
    name: "Имя и фамилия", email: "Email", category: "Категория", expertise: "Ваша экспертиза", price: "Цена за сессию ($)", links: "Портфолио / соцсети", message: "Расскажите о себе", submit: "Отправить заявку", success: "Заявка получена! Мы скоро свяжемся.", sending: "Отправка..." },
  ar: { title: "كن خبيراً", subtitle: "حوّل معرفتك إلى دخل. قابل العملاء حول العالم عبر مكالمات الفيديو.", back: "الرئيسية",
    perks: ["حدد سعرك", "أدر تقويمك", "استشارات فيديو فردية", "وصول لجمهور عالمي"],
    name: "الاسم الكامل", email: "البريد الإلكتروني", category: "الفئة", expertise: "مجال خبرتك", price: "السعر المستهدف للجلسة ($)", links: "روابط الأعمال / التواصل", message: "عرّف بنفسك", submit: "إرسال الطلب", success: "تم استلام طلبك! سنتواصل معك قريباً.", sending: "جاري الإرسال..." },
};

const input = "w-full bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-3 outline-none focus:border-indigo-500 focus:bg-white transition text-neutral-800";

const BecomeExpert = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const { toast } = useToast();
  const c = COPY[lang] || COPY.en;
  const [f, setF] = useState({ name: "", email: "", category: "career", expertise: "", price: "", links: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const perkIcons = [DollarSign, Globe, Video, Sparkles];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.addApplication(f); setDone(true); toast({ title: c.success }); }
    catch (err) { toast({ title: "Gönderim başarısız, tekrar dene." }); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-900 mb-6">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {c.back}
          </button>
          <div className="grid lg:grid-cols-5 gap-10">
            {/* left copy */}
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold mb-5"><Sparkles className="w-4 h-4" /> intro</span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-neutral-900 tracking-tight leading-[1.05]">{c.title}</h1>
              <p className="mt-4 text-lg text-neutral-500 leading-relaxed">{c.subtitle}</p>
              <ul className="mt-7 space-y-3">
                {c.perks.map((p, i) => {
                  const Icon = perkIcons[i];
                  return (
                    <li key={p} className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-xl bg-neutral-900 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-white" /></span>
                      <span className="text-neutral-700 font-medium">{p}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* right form */}
            <div className="lg:col-span-3">
              {done ? (
                <div className="bg-white rounded-3xl border border-neutral-100 shadow-xl p-10 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5"><Check className="w-8 h-8 text-emerald-600" /></div>
                  <h3 className="font-display font-bold text-2xl text-neutral-900">{c.success}</h3>
                  <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white px-6">{c.back}</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white rounded-3xl border border-neutral-100 shadow-xl p-6 sm:p-8 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className="text-sm font-medium text-neutral-700">{c.name}</label><input required className={`${input} mt-1.5`} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
                    <div><label className="text-sm font-medium text-neutral-700">{c.email}</label><input type="email" required className={`${input} mt-1.5`} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className="text-sm font-medium text-neutral-700">{c.category}</label>
                      <select className={`${input} mt-1.5`} value={f.category} onChange={(e) => set("category", e.target.value)}>
                        {CATEGORIES.map((cat) => <option key={cat.id} value={cat.id}>{translations[lang]?.categories?.[cat.key]}</option>)}
                      </select>
                    </div>
                    <div><label className="text-sm font-medium text-neutral-700">{c.price}</label><input type="number" className={`${input} mt-1.5`} value={f.price} onChange={(e) => set("price", e.target.value)} /></div>
                  </div>
                  <div><label className="text-sm font-medium text-neutral-700">{c.expertise}</label><input className={`${input} mt-1.5`} placeholder="Girişimcilik, Marka..." value={f.expertise} onChange={(e) => set("expertise", e.target.value)} /></div>
                  <div><label className="text-sm font-medium text-neutral-700">{c.links}</label><input className={`${input} mt-1.5`} placeholder="https://..." value={f.links} onChange={(e) => set("links", e.target.value)} /></div>
                  <div><label className="text-sm font-medium text-neutral-700">{c.message}</label><textarea rows={4} className={`${input} mt-1.5`} value={f.message} onChange={(e) => set("message", e.target.value)} /></div>
                  <Button type="submit" disabled={loading} className="w-full h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin me-1" /> : <Send className="w-4 h-4 me-1.5" />} {loading ? c.sending : c.submit}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <Toaster />
    </div>
  );
};

export default BecomeExpert;
